import {
  isValidHttpStatusCode,
  normalizeHttpHeaders,
  resolveEffectiveHttpVersion,
  resolveHarBodyForWireDisplay,
} from '@terra/shared-utils';
import type { ParsedHarEntry } from '../types';

const isLikelyDecodedJsonBody = (text: string): boolean => {
  const trimmed = text.trim();

  return trimmed.startsWith('{') || trimmed.startsWith('[');
};

const resolveBodyForHarEditorDisplay = ({
  text,
  mimeType,
}: {
  text: string;
  mimeType: string;
}): string => {
  if (isLikelyDecodedJsonBody(text)) {
    return text;
  }

  return resolveHarBodyForWireDisplay({
    text,
    mimeType,
  });
};

export function getHeaderContentType(
  headers: ParsedHarEntry['requestHeaders'],
): string {
  if (!headers) {
    return '';
  }

  const match = normalizeHttpHeaders(headers).find(
    (header) => header.name.toLowerCase() === 'content-type',
  );

  return match?.value ?? '';
}

export function formatHarRequestForEditor(entry: ParsedHarEntry): string {
  if (!entry.url) return '';

  let displayPath = '/';
  let host = '';

  try {
    const url = new URL(entry.url);

    displayPath = url.pathname + url.search;
    host = url.host;
  } catch {
    displayPath = entry.url;
  }

  const method = entry.method || 'GET';
  const requestHeaders =
    entry.requestHeaders && entry.requestHeaders.length > 0
      ? entry.requestHeaders
      : [
          ...(host ? [{ name: 'Host', value: host }] : []),
          ...(entry.postData && entry.postData !== 'N/A'
            ? [{ name: 'Content-Type', value: 'application/json' }]
            : []),
        ];
  const normalizedHeaders = normalizeHttpHeaders(requestHeaders);
  const headerLines = normalizedHeaders.map(
    (header) => `${header.name}: ${header.value}`,
  );

  const requestHttpVersion = resolveEffectiveHttpVersion({
    httpVersion: entry.requestHttpVersion,
    headers: requestHeaders,
  });
  let content = `${method.toUpperCase()} ${displayPath} ${requestHttpVersion}`;

  if (headerLines.length > 0) {
    content += `\n${headerLines.join('\n')}`;
  }

  if (entry.postData && entry.postData !== 'N/A') {
    const wireBody = resolveBodyForHarEditorDisplay({
      text: entry.postData,
      mimeType: getHeaderContentType(requestHeaders),
    });

    content += `\n\n${wireBody}`;
  }

  return content;
}

export function parseHarResponseStatus(
  responseStatus?: string,
): number | undefined {
  if (!responseStatus) {
    return undefined;
  }

  const parsed = Number.parseInt(responseStatus, 10);

  return isValidHttpStatusCode(parsed) ? parsed : undefined;
}

export function formatHarResponseForEditor(entry: ParsedHarEntry): string {
  const statusCode = parseHarResponseStatus(entry.responseStatus);

  if (statusCode === undefined) {
    return '';
  }

  const statusText = entry.responseStatusText || getStatusText(statusCode);
  const responseHeaders = entry.responseHeaders ?? [];
  const shouldAddSyntheticJsonContentType =
    responseHeaders.length === 0 &&
    !!entry.bodyPreview &&
    entry.bodyPreview !== 'N/A';
  const displayResponseHeaders = shouldAddSyntheticJsonContentType
    ? [{ name: 'Content-Type', value: 'application/json' }]
    : responseHeaders;
  const responseHttpVersion = resolveEffectiveHttpVersion({
    httpVersion: entry.responseHttpVersion,
    headers: responseHeaders,
  });
  let content = `${responseHttpVersion} ${statusCode} ${statusText}`;

  for (const header of normalizeHttpHeaders(responseHeaders)) {
    content += `\n${header.name}: ${header.value}`;
  }

  if (shouldAddSyntheticJsonContentType) {
    content += '\nContent-Type: application/json';
  }

  if (entry.bodyPreview && entry.bodyPreview !== 'N/A') {
    const wireBody = resolveBodyForHarEditorDisplay({
      text: entry.bodyPreview,
      mimeType: getHeaderContentType(displayResponseHeaders),
    });

    content += `\n\n${wireBody}`;
  }

  return content;
}

function getStatusText(code: number): string {
  const statusTexts: Record<number, string> = {
    200: 'OK',
    201: 'Created',
    204: 'No Content',
    301: 'Moved Permanently',
    302: 'Found',
    304: 'Not Modified',
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    405: 'Method Not Allowed',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
  };

  return statusTexts[code] || 'Unknown';
}
